import { Link } from "react-router-dom";
import { ArrowUpRight, Brain, Target, Zap, Eye, Mail } from "lucide-react";

const EMAIL_HREF =
  "mailto:hello@pixelkare.com?subject=AI%20App%20Development%20Enquiry&body=Hi%20Pixelkare%2C%20I%27d%20like%20to%20discuss%20building%20an%20AI-powered%20app.";

const PILLARS = [
  {
    icon: Brain,
    title: "AI-first engineering",
    body: "Architecture, codegen, testing — every stage of the SDLC is augmented by AI pipelines we built and tuned over years of shipping.",
  },
  {
    icon: Target,
    title: "Outcome over output",
    body: "We don't sell hours. We sell shipped, working, production-grade products. The clock stops when your app is live.",
  },
  {
    icon: Zap,
    title: "Radical speed",
    body: "A full app in seven days isn't a slogan — it's our default cycle. We compress what used to take quarters into a single week.",
  },
  {
    icon: Eye,
    title: "Transparent by design",
    body: "Fixed scope. Fixed price. Daily checkpoints. You always know what's being built and what it costs — no surprises, ever.",
  },
];

const TIMELINE = [
  {
    year: "2017",
    title: "Pixelkare is born",
    body: "Founded in Bhubaneswar as a custom web & mobile development studio.",
  },
  {
    year: "2020",
    title: "60+ apps shipped",
    body: "Scaled across e-commerce, healthcare, education, and corporate verticals.",
  },
  {
    year: "2023",
    title: "AI experiments begin",
    body: "Our team starts running AI-augmented build sprints — and the numbers stop making sense in the best way.",
  },
  {
    year: "2025",
    title: "AI-only pivot",
    body: "We retire the agency model. Pixelkare becomes a pure AI-powered app development studio.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-black/10">
        <div
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 70% 20%, rgba(31,135,188,0.12), transparent 55%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-28">
          <p className="overline reveal" data-testid="about-eyebrow">
            <span className="inline-block h-1.5 w-1.5 bg-[#1f87bc] mr-3 align-middle" />
            About Pixelkare
          </p>
          <h1
            data-testid="about-heading"
            className="font-display reveal text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] mt-8 max-w-5xl text-[#0a0a0a]"
            style={{ animationDelay: "100ms" }}
          >
            We rebuilt our studio
            <br />
            around <span className="text-[#1f87bc]">AI</span>.
          </h1>
          <p
            className="reveal mt-10 text-lg md:text-xl text-[#52525b] max-w-2xl leading-relaxed"
            style={{ animationDelay: "200ms" }}
          >
            Pixelkare started as a traditional web & mobile development agency.
            After watching AI quietly outperform the old way on speed, quality,
            and cost — we did something most agencies won't. We pivoted the
            entire business around it.
          </p>
        </div>
      </section>

      {/* WHY WE PIVOTED */}
      <section
        className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32"
        data-testid="about-story"
      >
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-5">
            <p className="overline">The shift</p>
            <h2 className="font-display text-4xl md:text-5xl mt-6 text-[#0a0a0a] leading-[1]">
              Why we stopped doing it the old way.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 space-y-6 text-[#52525b] leading-relaxed text-lg">
            <p>
              For years, our clients kept asking the same questions:{" "}
              <span className="text-[#0a0a0a]">why does it take so long?</span>{" "}
              Why does a "small change" cost so much? Why is there always a
              backlog of bugs at launch?
            </p>
            <p>
              The honest answer was: because that's how traditional agencies
              work. Humans, hours, handoffs. Every step adds delay and error.
            </p>
            <p>
              When we replaced parts of that pipeline with AI — architecture,
              codegen, testing — the math changed completely.{" "}
              <span className="text-[#0a0a0a]">
                A complex app stopped being a six-month project. It became a
                seven-day one.
              </span>{" "}
              Bugs collapsed. Costs collapsed. Risk collapsed. So we made AI
              the default, not the experiment.
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section
        className="border-t border-black/10 bg-[#f3f1ec]"
        data-testid="about-pillars"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="mb-16">
            <p className="overline">What we stand for</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-6 text-[#0a0a0a] leading-[1]">
              Four non-negotiables.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-white p-10 hover-lift hover:bg-[#fcfbf7]"
                >
                  <Icon className="h-6 w-6 text-[#1f87bc]" />
                  <h3 className="font-display text-3xl mt-6 text-[#0a0a0a]">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-[#52525b] leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section
        className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32"
        data-testid="about-timeline"
      >
        <p className="overline">A short timeline</p>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-6 text-[#0a0a0a] leading-[1]">
          From agency to AI studio.
        </h2>

        <div className="mt-16 border-t border-black/10">
          {TIMELINE.map((t) => (
            <div
              key={t.year}
              data-testid={`timeline-${t.year}`}
              className="grid grid-cols-12 gap-6 border-b border-black/10 py-8 hover:bg-black/[0.02] transition-colors"
            >
              <div className="col-span-12 md:col-span-2">
                <p className="font-mono text-sm tracking-[0.2em] text-[#1f87bc]">
                  {t.year}
                </p>
              </div>
              <div className="col-span-12 md:col-span-4">
                <h3 className="font-display text-2xl text-[#0a0a0a]">
                  {t.title}
                </h3>
              </div>
              <div className="col-span-12 md:col-span-6">
                <p className="text-[#52525b] leading-relaxed">{t.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-black/10 bg-[#f3f1ec]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-28">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 md:col-span-8">
              <h2 className="font-display text-4xl md:text-6xl text-[#0a0a0a] leading-[1]">
                Got an idea? <br />
                <span className="text-[#a1a1aa]">Let's ship it </span>
                <span className="text-[#1f87bc]">this week.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 flex flex-col md:items-end gap-4">
              <a
                href={EMAIL_HREF}
                data-testid="about-cta-email"
                className="inline-flex items-center gap-3 bg-[#1f87bc] hover:bg-[#176d99] text-white px-7 py-4 font-medium transition-colors"
              >
                <Mail className="h-5 w-5" />
                Email us
              </a>
              <Link
                to="/contact"
                data-testid="about-cta-contact"
                className="inline-flex items-center gap-3 text-[#52525b] hover:text-[#0a0a0a] transition-colors font-mono uppercase text-xs tracking-[0.2em]"
              >
                Or visit contact page
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
